import { useTranslation } from "@outschool/localization";

import { NEW_FROM_TITLE } from "./NewFrom";
import { PREFERRED_TIME_TITLE } from "./PreferredTimes";
import { SPECIAL_TITLE, TOPIC_TITLE } from "./TopicDefinitions";

export type NewFromTitleType =
  (typeof NEW_FROM_TITLE)[keyof typeof NEW_FROM_TITLE];

export type PreferredTimeTitleType =
  (typeof PREFERRED_TIME_TITLE)[keyof typeof PREFERRED_TIME_TITLE];

export type TopicTitleType =
  | (typeof SPECIAL_TITLE)[keyof typeof SPECIAL_TITLE]
  | (typeof TOPIC_TITLE)[keyof typeof TOPIC_TITLE];

export type TitleType =
  | NewFromTitleType
  | TopicTitleType
  | PreferredTimeTitleType;

const softExhausiveCheck = (title: never) => {
  OsPlatform.captureError(`${title} is unhandled`);
};

export const useCardTitleTranslation = (
  title?: NewFromTitleType | TopicTitleType | PreferredTimeTitleType,
  isCategory = false
): string => {
  const { t } = useTranslation(
    "shared\\parentDashboard\\cardTypes\\useCardTitleTranslation"
  );
  if (isCategory && title) {
    return title;
  }
  switch (title) {
    case undefined:
      return "";
    case NEW_FROM_TITLE.SAVED_CLASSES:
      return t("Upcoming and saved classes");
    case NEW_FROM_TITLE.FAVORITE_TEACHERS:
      return t("Upcoming classes from teachers you follow");
    case PREFERRED_TIME_TITLE.AFTERSCHOOL:
      return t("Great afterschool classes");
    case PREFERRED_TIME_TITLE.DAYTIME:
      return t("Great daytime classes");
    case PREFERRED_TIME_TITLE.WEEKEND:
      return t("Great weekend classes");
    case SPECIAL_TITLE.BESTSELLERS:
      return t("Best-sellers starting soon");
    case TOPIC_TITLE.ACTING_DRAMA:
      return t("Acting and drama");
    case TOPIC_TITLE.ALGEBRA:
      return t("Algebra");
    case TOPIC_TITLE.AMERICAN_HISTORY:
      return t("American history");
    case TOPIC_TITLE.AMONG_US:
      return t("Play Among Us");
    case TOPIC_TITLE.ANIMALS_AND_WILDLIFE:
      return t("Animals and wildlife");
    case TOPIC_TITLE.BALLET:
      return t("Ballet");
    case TOPIC_TITLE.BIOLOGY:
      return t("Explore biology");
    case TOPIC_TITLE.BOOK_CLUBS:
      return t("Book clubs");
    case TOPIC_TITLE.CALCULUS:
      return t("Calculus");
    case TOPIC_TITLE.CAREERS:
      return t("Exploring careers");
    case TOPIC_TITLE.CARTOON_DRAWING:
      return t("Cartoon drawing");
    case TOPIC_TITLE.CHEMISTRY:
      return t("Explore chemistry");
    case TOPIC_TITLE.CHINESE:
      return t("Learn Chinese");
    case TOPIC_TITLE.CIRCLE_TIME:
      return t("Circle time");
    case TOPIC_TITLE.CODING_AND_PROGRAMING:
      return t("Coding and programming");
    case TOPIC_TITLE.COOKING_AND_BAKING:
      return t("Cooking and baking");
    case TOPIC_TITLE.COUNTING_AND_NUMBERS:
      return t("Counting and numbers");
    case TOPIC_TITLE.CRAFTS:
      return t("Arts and crafts");
    case TOPIC_TITLE.CREATIVE_WRITING:
      return t("Creative writing");
    case TOPIC_TITLE.DANCE:
      return t("Explore dance");
    case TOPIC_TITLE.DEBATE:
      return t("Debate skills");
    case TOPIC_TITLE.DINOSAURS:
      return t("Dinosaurs");
    case TOPIC_TITLE.DOGS:
      return t("Dogs");
    case TOPIC_TITLE.DRAWING:
      return t("All things drawing");
    case TOPIC_TITLE.DRAW_ANIMALS:
      return t("Learn to draw animals");
    case TOPIC_TITLE.DRAW_PEOPLE:
      return t("Learn to draw people");
    case TOPIC_TITLE.DRAWING_TECHNIQUES:
      return t("Drawing techniques");
    case TOPIC_TITLE.DUNGEONS_AND_DRAGONS:
      return t("Dungeons and Dragons");
    case TOPIC_TITLE.MANAGING_EMOTIONS:
      return t("Managing emotions");
    case TOPIC_TITLE.ESCAPE_ROOMS:
      return t("Escape rooms");
    case TOPIC_TITLE.ESSAY_WRITING:
      return t("Essay writing");
    case TOPIC_TITLE.FRENCH:
      return t("Learn French");
    case TOPIC_TITLE.VIDEO_GAMES:
      return t("Video games");
    case TOPIC_TITLE.GEOGRAPHY:
      return t("Explore geography");
    case TOPIC_TITLE.GEOMETRY:
      return t("Geometry");
    case TOPIC_TITLE.HISTORY:
      return t("Explore history");
    case TOPIC_TITLE.INVESTING:
      return t("Investing");
    case TOPIC_TITLE.JAPANESE:
      return t("Learn Japanese");
    case TOPIC_TITLE.MATH:
      return t("Mastering math");
    case TOPIC_TITLE.MINECRAFT:
      return t("Learning with Minecraft");
    case TOPIC_TITLE.MONEY:
      return t("Money skills");
    case TOPIC_TITLE.MUTLIPLICATION:
      return t("Multiplication");
    case TOPIC_TITLE.PAINTING:
      return t("All things painting");
    case TOPIC_TITLE.PHONICS:
      return t("Phonics");
    case TOPIC_TITLE.PHOTOGRAPHY:
      return t("Photography");
    case TOPIC_TITLE.PROCREATE:
      return t("Draw with Procreate");
    case TOPIC_TITLE.PSYCHOLOGY:
      return t("Psychology");
    case TOPIC_TITLE.READING:
      return t("Reading");
    case TOPIC_TITLE.ROBLOX:
      return t("Learning with Roblox");
    case TOPIC_TITLE.SCIENCE:
      return t("Exploring science");
    case TOPIC_TITLE.SIGN_LANGUAGE:
      return t("Learn sign language");
    case TOPIC_TITLE.SINGING:
      return t("Singing");
    case TOPIC_TITLE.SKETCH:
      return t("Learn to sketch");
    case TOPIC_TITLE.SPANISH:
      return t("Learn Spanish");
    case TOPIC_TITLE.SPANISH_IMMERSION:
      return t("Spanish immersion");
    case TOPIC_TITLE.THEATER:
      return t("Theater");
    case TOPIC_TITLE.WORKOUTS_AND_FITNESS:
      return t("Workouts and fitness");
    case TOPIC_TITLE.WORLD_HISTORY:
      return t("World history");
    case TOPIC_TITLE.WRITING:
      return t("Writing skills");
    case TOPIC_TITLE.SUMMER_CAMPS:
      return t("Summer camps");
    case TOPIC_TITLE.TRAVEL:
      return t("Travel the globe");
    default:
      softExhausiveCheck(title);
      return title;
  }
};
